import { ReactNode, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { namespaces, useSharedTranslation } from 'us-react-components/dist/hooks/useSharedTranslation';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook } from '@fortawesome/free-brands-svg-icons/faFacebook';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';

import {
  UserService,
  UserPlanService,
  ParentService,
  PlanService,
  Plan,
  GoogleConnectionService,
  SavedSearchService,
  SavedSearch as SavedSearchType,
} from 'us-web-services';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import { PasswordChangeForm } from 'us-react-components/dist/PasswordChangeForm/PasswordChangeForm';
import { PASSWORD_RE } from 'us-react-components/dist/PasswordChangeForm/passwordValidation';
import { ToggleSwitch } from 'us-react-components/dist/ToggleSwitch/ToggleSwitch';
import { getLongDateString } from 'us-react-components/dist/utils/date';
import { fullName } from 'us-react-components/dist/utils/string';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { FacebookSdk } from 'us-react-components/dist/utils/FacebookSdk';

import { FacebookUtil } from '../../utils/FacebookUtil';

const parseJwt = (token: string): { sub?: string } | null => {
  try {
    const payload = token.split('.')[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

interface ParentPlan {
  id?: number;
  recurringFee?: number;
  end?: string;
  cancelAt?: string;
  plan?: { id?: number; name?: string; frequency?: string };
}

interface SettingsSectionProps {
  id: string;
  title: string;
  children: ReactNode;
}

const SettingsSection = ({ id, title, children }: SettingsSectionProps): JSX.Element => {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <div id={id} className='bg-white border border-neutral-200 rounded-lg shadow-sm'>
      <div className='flex items-center justify-between px-5 py-4 border-b border-neutral-200'>
        <h2 className='text-xl font-semibold m-0'>{title}</h2>
        <button
          type='button'
          aria-label={collapsed ? `Expand ${title}` : `Collapse ${title}`}
          aria-expanded={!collapsed}
          onClick={(): void => setCollapsed((v) => !v)}
          className='w-8 h-8 flex items-center justify-center text-neutral-600 hover:text-neutral-900'
        >
          <FontAwesomeIcon icon={collapsed ? faChevronDown : faChevronUp} />
        </button>
      </div>
      {!collapsed && <div className='p-5'>{children}</div>}
    </div>
  );
};

export const Settings = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.family);
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores, services: { config } } = useDependencies();
  const user = stores.user.user;
  const parent = stores.parent.parent as (typeof stores.parent.parent) & {
    friendRecommendationId?: number;
    phone?: string;
    schools?: Array<{ id: number; school?: { name?: string }; recommendationNotificationId?: number }>;
    affiliations?: Array<{ id: number; affiliation?: { name?: string }; recommendationNotificationId?: number }>;
    localGroups?: Array<{ id: number; localGroup?: { name?: string }; recommendationNotificationId?: number }>;
    companyEmployee?: unknown;
    verification?: { passed?: boolean; methods?: { facebook?: boolean; stripe?: boolean } };
    showBookedSitters?: boolean;
  };

  const [email, setEmail] = useState<string>(user?.email ?? '');
  const [phone, setPhone] = useState<string>(parent?.phone ?? '');
  const [changingPassword, setChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [currentPlan, setCurrentPlan] = useState<ParentPlan | null>(null);
  const [activeSearches, setActiveSearches] = useState<SavedSearchType[]>([]);

  // Mirror Angular parent-settings.fetchActiveSearches: pull the parent's
  // active saved searches so the Saved Searches module renders on settings.
  useEffect(() => {
    if (!user?.id) return;
    const load = async (): Promise<void> => {
      try {
        const resp = await SavedSearchService.getSavedSearchByFilter({
          params: { active: true, limit: 3, parentId: user.id },
        });
        setActiveSearches(resp.data?.data ?? []);
      } catch {
        setActiveSearches([]);
      }
    };
    void load();
  }, [user?.id]);
  const [changeModalOpen, setChangeModalOpen] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    document.title = 'UrbanSitter - Settings';
  }, []);

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user?.email]);

  useEffect(() => {
    if (parent?.phone) setPhone(parent.phone);
  }, [parent?.phone]);

  useEffect(() => {
    if (!user?.id) return;
    const loadCurrentPlan = async (): Promise<void> => {
      try {
        const resp = await UserPlanService.getByFilter({ params: { userId: user.id, active: true } });
        const plansList = (resp.data?.data ?? []) as ParentPlan[];
        setCurrentPlan(plansList[0] ?? null);
      } catch {
        setCurrentPlan(null);
      }
    };
    const loadPlans = async (): Promise<void> => {
      try {
        const resp = await PlanService.getByFilter({ params: { limit: 3, parentId: user.id, userId: user.id } });
        setPlans((resp.data?.data ?? []) as Plan[]);
      } catch {
        setPlans([]);
      }
    };
    void loadCurrentPlan();
    void loadPlans();
  }, [user?.id]);

  useEffect(() => {
    const userId = user?.id;
    if (!userId || user?.googleUserId) return undefined;
    const clientId = config?.GOOGLE_APP_ID;
    if (!clientId) return undefined;

    const linkGoogle = (response: { credential?: string }): void => {
      if (!response.credential) {
        toast.error(t('pages.settings.google.linkFailed'));
        return;
      }
      const jwt = parseJwt(response.credential);
      if (!jwt?.sub) {
        toast.error(t('pages.settings.google.linkFailed'));
        return;
      }
      void (async (): Promise<void> => {
        try {
          await GoogleConnectionService.create({
            userId,
            googleUser: {
              accessToken: response.credential,
              googleId: jwt.sub,
              userId,
            },
          });
          toast.success(t('pages.settings.google.linkSuccess'));
          void stores.user.refreshUser?.();
        } catch {
          toast.error(t('pages.settings.google.linkError'));
        }
      })();
    };

    let cancelled = false;
    const render = (): void => {
      if (cancelled) return;
      const el = document.getElementById('link-google-parent');
      if (!el || !window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        auto_select: false,
        callback: linkGoogle,
        client_id: clientId,
      });
      window.google.accounts.id.renderButton(el, {
        shape: 'pill',
        size: 'large',
        text: 'continue_with',
        theme: 'outline',
        use_fedcm_for_button: true,
      });
    };

    if (window.google?.accounts?.id) {
      render();
    } else {
      const interval = window.setInterval(() => {
        if (window.google?.accounts?.id) {
          window.clearInterval(interval);
          render();
        }
      }, 200);
      return (): void => {
        cancelled = true;
        window.clearInterval(interval);
      };
    }

    return (): void => {
      cancelled = true;
    };
  }, [user?.id, user?.googleUserId, config?.GOOGLE_APP_ID, stores.user]);

  const pid = Number(pathId);
  if (pid && user?.id && pid !== user.id) {
    navigate(`/parent/${user.id}/settings`, { replace: true });
  }

  const save = async (): Promise<void> => {
    if (!user?.id || saving) return;
    if (changingPassword) {
      if (!newPassword || newPassword !== confirmPassword) {
        toast.error(t('components.passwordChange.passwordMismatch'));
        return;
      }
      if (!PASSWORD_RE.test(newPassword)) {
        toast.error(t('components.passwordChange.requirements'));
        return;
      }
    }
    setSaving(true);
    try {
      const body: {
        id: number;
        email?: string;
        password?: string;
        currentPassword?: string;
      } = { id: user.id };
      if (email && email !== user.email) body.email = email;
      if (changingPassword && newPassword) {
        body.password = newPassword;
        if (user.passwordChanged) body.currentPassword = currentPassword;
      }
      await UserService.patch(user.id, body as never);

      if (parent?.id && phone !== parent.phone) {
        await ParentService.patch(parent.id, { ...parent, phone } as never);
      }

      await stores.user.refreshUser?.();
      toast.success(t('pages.settings.savedToast'));
      setChangingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch {
      toast.error(t('pages.settings.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const changePlan = async (): Promise<void> => {
    if (!user?.id || !selectedPlanId || !currentPlan?.id) return;
    if (!termsAccepted) {
      toast.error(t('pages.settings.planModal.acceptTerms'));
      return;
    }
    setChanging(true);
    try {
      await UserPlanService.patch(currentPlan.id, {
        id: currentPlan.id,
        planId: selectedPlanId,
      });
      toast.success(t('pages.settings.planModal.planUpdated'));
      setChangeModalOpen(false);
      const resp = await UserPlanService.getByFilter({
        params: { userId: user.id, active: true },
      });
      const list = (resp.data?.data ?? []) as ParentPlan[];
      setCurrentPlan(list[0] ?? null);
    } catch {
      toast.error(t('pages.settings.planModal.planUpdateError'));
    } finally {
      setChanging(false);
    }
  };

  const toggleNewsletter = async (): Promise<void> => {
    if (!user?.id) return;
    try {
      await UserService.patch(user.id, {
        id: user.id,
        emailNewsletter: !user.emailNewsletter,
      } as never);
      await stores.user.refreshUser?.();
    } catch {
      toast.error(t('pages.settings.saveError'));
    }
  };

  const toggleFriendNotifications = async (): Promise<void> => {
    if (!parent?.id) return;
    const curr = parent.friendRecommendationId ?? 1;
    try {
      await ParentService.patch(parent.id, {
        ...parent,
        friendRecommendationId: curr === 2 ? 1 : 2,
      } as never);
    } catch {
      toast.error(t('pages.settings.saveError'));
    }
  };

  const toggleShowBookedSitters = async (): Promise<void> => {
    if (!parent?.id) return;
    const next = !parent.showBookedSitters;
    try {
      await ParentService.patch(parent.id, {
        id: parent.id,
        showBookedSitters: next,
      } as never);
      toast.success(t('pages.settings.savedToast'));
    } catch {
      toast.error(t('pages.settings.saveError'));
    }
  };

  const saveGroupNotification = async (
    kind: 'schools' | 'affiliations' | 'localGroups',
    item: { id: number; recommendationNotificationId?: number },
  ): Promise<void> => {
    if (!parent?.id) return;
    const next = (item.recommendationNotificationId ?? 1) === 2 ? 1 : 2;
    try {
      const list = (parent[kind] as Array<{ id: number; recommendationNotificationId?: number }>) ?? [];
      await ParentService.patch(parent.id, {
        ...parent,
        [kind]: list.map((i) =>
          i.id === item.id ? { ...i, recommendationNotificationId: next } : i,
        ),
      } as never);
    } catch {
      toast.error(t('pages.settings.saveError'));
    }
  };

  if (!user?.id) {
    return (
      <div className='container py-8'>
        <i className='fa-solid fa-spinner fa-spin mr-2' />
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  const passedVerification = parent?.verification?.passed;
  const verificationMethods = parent?.verification?.methods;
  const canVerify = parent?.verification && !passedVerification && verificationMethods &&
    (verificationMethods.facebook || verificationMethods.stripe);
  const hasActivePlan = !!currentPlan && !currentPlan.cancelAt;
  const isCanceled = !!currentPlan?.cancelAt;
  const isRecurring = !!currentPlan?.plan?.frequency;
  const schools = (parent?.schools ?? []) as Array<{ id: number; school?: { name?: string }; recommendationNotificationId?: number }>;
  const affiliations = (parent?.affiliations ?? []) as Array<{ id: number; affiliation?: { name?: string }; recommendationNotificationId?: number }>;
  const localGroups = (parent?.localGroups ?? []) as Array<{ id: number; localGroup?: { name?: string }; recommendationNotificationId?: number }>;
  const hasCompanyEmployee = !!parent?.companyEmployee;

  return (
    <div className='container py-6 max-w-5xl mx-auto'>
      <h1 className='text-2xl font-semibold mb-5'>{t('common.settings')}</h1>

      <div className='grid grid-cols-1 md:grid-cols-12 gap-6'>
        <div className='md:col-span-9 space-y-6'>
          {/* Account Basics */}
          <SettingsSection id='account-settings-basics' title={t('pages.settings.sections.accountBasics')}>

            <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4'>
              <h3 className='text-base font-semibold m-0'>{t('common.name')}</h3>
              <div className='sm:col-span-2'>
                <div>{fullName(parent) || user.firstName}</div>
                <div id='joined-date' className='text-xs text-neutral-500'>
                  Joined {getLongDateString(user.created)}
                </div>
              </div>
            </div>

            {!hasCompanyEmployee && (
              <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4'>
                <h3 className='text-base font-semibold m-0'>{t('pages.settings.rowHeadings.membershipPlan')}</h3>
                <div className='sm:col-span-2'>
                  {!currentPlan || !hasActivePlan ? (
                    <span id='no-membership-plan'>
                      None (
                      <button
                        type='button'
                        id='upgrade-access-link'
                        className='underline text-sapphire'
                        onClick={(): void => navigate(`/parent/${user.id}/buy-membership`)}
                      >
                        Add a plan
                      </button>
                      )
                    </span>
                  ) : (
                    <span id='membership-plan'>
                      {currentPlan.plan?.name}
                      {currentPlan.recurringFee ? (
                        <> - ${currentPlan.recurringFee} {currentPlan.plan?.frequency}</>
                      ) : null}
                    </span>
                  )}
                  {isCanceled && (
                    <>
                      {' - '}
                      <span className='text-red-600'>Canceled</span>
                    </>
                  )}
                  {currentPlan?.end && (
                    <div>Active until {getLongDateString(currentPlan.end)}</div>
                  )}
                  {currentPlan && hasActivePlan && (
                    <div className='mt-1'>
                      <Button
                        buttonType={ButtonType.tertiary}
                        id='change-membership-plan'
                        className='underline text-sm cursor-pointer'
                        onClick={(): void => {
                          setSelectedPlanId(currentPlan?.plan?.id ?? null);
                          setTermsAccepted(false);
                          setChangeModalOpen(true);
                        }}
                      >
                        Change or Cancel Membership
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {currentPlan && !isCanceled && hasActivePlan && isRecurring && !hasCompanyEmployee && (
              <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4'>
                <h3 className='text-base font-semibold m-0'>{t('pages.settings.rowHeadings.nextBillingDate')}</h3>
                <div id='billing-info' className='sm:col-span-2'>
                  {getLongDateString(currentPlan.end)}
                </div>
              </div>
            )}

            <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4'>
              <h3 className='text-base font-semibold m-0'>{t('pages.settings.rowHeadings.identityCheck')}</h3>
              <div id='id-check-status' className='sm:col-span-2'>
                {passedVerification ? (
                  <span>Completed</span>
                ) : canVerify ? (
                  <a
                    id='open-id-check-modal'
                    href='/app/complete-identity-verification'
                    className='underline'
                  >
                    Complete an identity check
                  </a>
                ) : parent?.verification && !passedVerification ? (
                  <span>
                    Please contact{' '}
                    <a id='contact-support' href='mailto:support@urbansitter.com' className='underline'>
                      support@urbansitter.com
                    </a>{' '}
                    for help
                  </span>
                ) : null}
              </div>
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4'>
              <h3 className='text-base font-semibold m-0'>{t('pages.settings.rowHeadings.email')}</h3>
              <div className='sm:col-span-2'>
                <input
                  id='user-email-input'
                  aria-label={t('pages.settings.fields.emailAriaLabel')}
                  className='form-control w-full border border-neutral-300 rounded px-3 py-2'
                  type='email'
                  value={email}
                  onChange={(e): void => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4'>
              <h3 className='text-base font-semibold m-0'>{t('pages.settings.rowHeadings.password')}</h3>
              <div className='sm:col-span-2'>
                {!changingPassword ? (
                  <Button
                    buttonType={ButtonType.tertiary}
                    id='create-or-change-password'
                    onClick={(): void => setChangingPassword(true)}
                    className='underline cursor-pointer'
                  >
                    {user.passwordChanged ? 'Change' : 'Create'} Password
                  </Button>
                ) : (
                  <PasswordChangeForm
                    hasExistingPassword={!!user.passwordChanged}
                    currentPassword={currentPassword}
                    newPassword={newPassword}
                    confirmPassword={confirmPassword}
                    onCurrentPasswordChange={setCurrentPassword}
                    onNewPasswordChange={setNewPassword}
                    onConfirmPasswordChange={setConfirmPassword}
                    onCancel={(): void => {
                      setChangingPassword(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                  />
                )}
              </div>
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4'>
              <h3 className='text-base font-semibold m-0'>{t('pages.settings.rowHeadings.mobilePhone')}</h3>
              <div className='sm:col-span-2'>
                <input
                  id='user-phone-input'
                  className='form-control w-full border border-neutral-300 rounded px-3 py-2'
                  type='tel'
                  maxLength={12}
                  placeholder='xxx-xxx-xxxx'
                  value={phone}
                  onChange={(e): void => setPhone(e.target.value)}
                />
                <p className='italic text-sm mt-2'>
                  Optional. By providing your mobile phone number, you consent to UrbanSitter
                  sending you text message communications. Your number will be shared only
                  with caregivers you choose to book, and as otherwise permitted in our{' '}
                  <a id='privacy-policy' href='/privacy-policy' target='_blank' rel='noreferrer' className='underline'>
                    Privacy Policy
                  </a>
                  .
                </p>
              </div>
            </div>

            <div className='text-right'>
              <Button buttonType={ButtonType.primary} onClick={save} disabled={saving}>
                Save Changes
              </Button>
            </div>
          </SettingsSection>

          {/* Privacy Settings — mirrors Angular order: directly after Account Basics. */}
          <SettingsSection id='parent-privacy' title={t('pages.settings.privacy.heading')}>
            <h3 className='font-normal mb-3 m-0 text-base'>
              {t('pages.settings.privacy.description')}
            </h3>
            <div className='flex items-center gap-3'>
              <span className='font-semibold'>{t('pages.settings.privacy.showBookedCaregivers')}</span>
              <ToggleSwitch
                id='show-booked-caregivers'
                checked={!!parent?.showBookedSitters}
                handleClick={toggleShowBookedSitters}
              />
            </div>
          </SettingsSection>

          {/* Communication */}
          <SettingsSection id='account-settings-communications' title={t('pages.settings.sections.communication')}>
            <p className='text-sm mb-4'>
              Please note that independent of your selections below you will still receive
              emails related to any job posts, messages, bookings, or purchases you initiate
              on UrbanSitter.
            </p>

            <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6'>
              <h3 className='text-base font-semibold m-0'>{t('pages.settings.rowHeadings.newsletter')}</h3>
              <div className='sm:col-span-2'>
                <label className='flex items-start gap-2'>
                  <input
                    id='account-settings-newsletter'
                    type='checkbox'
                    checked={!!user.emailNewsletter}
                    onChange={toggleNewsletter}
                  />
                  <span>Send me a weekly newsletter</span>
                </label>
              </div>
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6'>
              <h3 className='text-base font-semibold m-0'>{t('pages.settings.rowHeadings.friendsOnUS')}</h3>
              <div className='sm:col-span-2'>
                <label className='flex items-start gap-2'>
                  <input
                    id='account-settings-friend-notifications'
                    type='checkbox'
                    checked={(parent?.friendRecommendationId ?? 1) === 2}
                    onChange={toggleFriendNotifications}
                  />
                  <span>Send me emails when friends leave a 5-star review</span>
                </label>
              </div>
            </div>

            {schools.length > 0 && (
              <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6'>
                <h3 className='text-base font-semibold m-0'>{t('pages.settings.rowHeadings.schools')}</h3>
                <div className='sm:col-span-2'>
                  <p className='text-sm mb-2'>
                    Send me emails when someone from these schools leaves a 5-star review
                  </p>
                  {schools.map((item) => (
                    <label key={item.id} className='flex items-start gap-2 mb-1'>
                      <input
                        id={`school-notification-${item.id}`}
                        type='checkbox'
                        checked={(item.recommendationNotificationId ?? 1) === 2}
                        onChange={(): Promise<void> => saveGroupNotification('schools', item)}
                      />
                      <span>{item.school?.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {affiliations.length > 0 && (
              <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6'>
                <h3 className='text-base font-semibold m-0'>{t('pages.settings.rowHeadings.affiliations')}</h3>
                <div className='sm:col-span-2'>
                  <p className='text-sm mb-2'>
                    Send me emails when someone from these affiliations leaves a 5-star review
                  </p>
                  {affiliations.map((item) => (
                    <label key={item.id} className='flex items-start gap-2 mb-1'>
                      <input
                        id={`affiliation-notification-${item.id}`}
                        type='checkbox'
                        checked={(item.recommendationNotificationId ?? 1) === 2}
                        onChange={(): Promise<void> => saveGroupNotification('affiliations', item)}
                      />
                      <span>{item.affiliation?.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {localGroups.length > 0 && (
              <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6'>
                <h3 className='text-base font-semibold m-0'>{t('pages.settings.rowHeadings.localGroups')}</h3>
                <div className='sm:col-span-2'>
                  <p className='text-sm mb-2'>
                    Send me emails when someone from these groups leaves a 5-star review
                  </p>
                  {localGroups.map((item) => (
                    <label key={item.id} className='flex items-start gap-2 mb-1'>
                      <input
                        id={`local-group-notification-${item.id}`}
                        type='checkbox'
                        checked={(item.recommendationNotificationId ?? 1) === 2}
                        onChange={(): Promise<void> => saveGroupNotification('localGroups', item)}
                      />
                      <span>{item.localGroup?.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {activeSearches.length > 0 && (
              <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6'>
                <h3 className='text-base font-semibold m-0'>{t('pages.settings.rowHeadings.savedSearches')}</h3>
                <div className='sm:col-span-2'>
                  {activeSearches.map((s) => (
                    <div key={s.id} className='mb-2'>
                      <a
                        href={`/app/parent/${user?.id}/saved-searches`}
                        className='text-sapphire hover:underline'
                      >
                        {s.title || s.name || `Search #${s.id}`}
                      </a>
                    </div>
                  ))}
                  <a
                    href={`/app/parent/${user?.id}/saved-searches`}
                    className='inline-block mt-2 text-sm font-semibold text-sapphire hover:underline'
                  >
                    Manage saved searches
                  </a>
                </div>
              </div>
            )}
          </SettingsSection>

          {/* Social Connections */}
          <SettingsSection id='social-accounts' title={t('pages.settings.sections.socialConnections')}>
            <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4'>
              <h3 className='text-base font-semibold m-0'>{t('pages.settings.rowHeadings.facebook')}</h3>
              <div className='sm:col-span-2'>
                {user.facebookId ? (
                  <span>{t('common.connected')}</span>
                ) : (
                  <>
                    <p className='mb-2'>{t('pages.settings.facebookConnectBody')}</p>
                    <Button
                      id='link-facebook'
                      buttonType={ButtonType.facebook}
                      onClick={(): void => {
                        void (async (): Promise<void> => {
                          try {
                            const sdk = await FacebookSdk.ensureReady();

                            sdk.login(
                              (response): void => {
                                FacebookUtil.linkFacebook(
                                  response,
                                  stores.user,
                                  t,
                                  () => stores.user.refreshUser(),
                                );
                              },
                              { scope: 'public_profile, email, user_friends' },
                            );
                          } catch {
                            toast.error(t('pages.settings.facebookSdkUnavailable'));
                          }
                        })();
                      }}
                    >
                      <FontAwesomeIcon icon={faFacebook} className='mr-2 text-lg align-middle' />
                      {t('pages.settings.connectFacebook')}
                    </Button>
                  </>
                )}
              </div>
            </div>
            <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
              <h3 className='text-base font-semibold m-0'>{t('pages.settings.rowHeadings.google')}</h3>
              <div className='sm:col-span-2'>
                {user.googleUserId ? (
                  <span>{t('common.connected')}</span>
                ) : (
                  <div id='link-google-parent' className='g_id_signin' />
                )}
              </div>
            </div>
          </SettingsSection>

        </div>

        {/* Upgrade sidebar */}
        {!hasActivePlan && (
          <div id='upgrade-sidebar' className='md:col-span-3'>
            <div className='membership-upsell border-t border-b py-4'>
              <h3 className='font-semibold mb-2'>{t('pages.settings.upsell.heading')}</h3>
              <p className='mb-2 font-medium'>{t('pages.settings.upsell.getAPlan')}</p>
              <ul className='text-sm mb-4 list-disc pl-5 space-y-1'>
                {plans.length > 0
                  ? plans.map((p) => <li key={p.id}>{p.label}</li>)
                  : (
                    <>
                      <li>{t('pages.settings.upsell.fallbackBullet1')}</li>
                      <li>{t('pages.settings.upsell.fallbackBullet2')}</li>
                      <li>{t('pages.settings.upsell.fallbackBullet3')}</li>
                    </>
                  )}
              </ul>
              <Button
                id='upgrade-membership'
                buttonType={ButtonType.primary}
                onClick={(): void => {
                  navigate(`/parent/${user.id}/buy-membership`);
                }}
                className='w-full'
              >
                {t('pages.settings.upsell.upgrade')}
              </Button>
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={changeModalOpen}
        onClose={(): void => setChangeModalOpen(false)}
        title={t('pages.settings.planModal.cancelHeading')}
        className='w-full max-w-lg'
        footer={
          <Button
            id='save-plan-change'
            buttonType={ButtonType.primary}
            disabled={
              changing ||
              !selectedPlanId ||
              selectedPlanId === currentPlan?.plan?.id ||
              !termsAccepted
            }
            onClick={changePlan}
          >
            {t('common.save')}
          </Button>
        }
      >
        <p className='mb-4 text-neutral-800'>
          {t('pages.settings.planModal.cancelBody')}
        </p>
        <div className='mb-6'>
          <Button
            buttonType={ButtonType.primary}
            id='cancel-membership'
            onClick={(): void => {
              setChangeModalOpen(false);
              navigate('/cancel-reasons');
            }}
          >
            {t('pages.settings.planModal.cancelCta')}
          </Button>
        </div>

        <hr className='my-4 border-neutral-200' />

        <h4 className='text-xl font-semibold mb-1'>
          {t('pages.settings.planModal.changeHeading')}
        </h4>
        <p className='mb-4 text-neutral-800'>
          {t('pages.settings.planModal.changeIntro')}
        </p>

        <div className='space-y-2 mb-4'>
          {plans.map((p) => {
            const isCurrent = p.id === currentPlan?.plan?.id;
            const active = p.id === selectedPlanId;
            return (
              <button
                key={p.id}
                type='button'
                onClick={(): void => {
                  if (p.id) setSelectedPlanId(p.id);
                }}
                className={`w-full text-left rounded-lg p-3 transition-colors ${
                  active
                    ? 'border-2 border-sapphire bg-sapphire-50'
                    : 'border border-neutral-300 hover:border-neutral-400 bg-white'
                }`}
              >
                <div className='flex items-baseline justify-between gap-2'>
                  <h3 className='text-base font-semibold m-0 text-neutral-900'>
                    {p.name}{' '}
                    {isCurrent && (
                      <span className='text-sm font-normal text-neutral-700'>(current)</span>
                    )}
                  </h3>
                  <div className='font-bold text-neutral-900'>
                    ${p.fee}
                    <span className='text-sm font-normal text-neutral-700'>
                      {p.frequency === 'monthly' ? '/mo' : p.frequency === 'yearly' ? '/yr' : ''}
                    </span>
                  </div>
                </div>
                {p.description && (
                  <div className='text-sm text-neutral-700 mt-1'>{p.description}</div>
                )}
              </button>
            );
          })}
        </div>

        <div className='border border-neutral-200 bg-neutral-50 rounded-lg p-3 mb-4'>
          <p className='text-xs text-neutral-700 mb-3'>
            {t('pages.settings.planModal.termsBody')}
          </p>
          <label
            htmlFor='change-membership-terms-checkbox'
            className='flex items-start gap-2 text-sm cursor-pointer'
          >
            <input
              id='change-membership-terms-checkbox'
              type='checkbox'
              checked={termsAccepted}
              onChange={(e): void => setTermsAccepted(e.target.checked)}
              className='mt-1 flex-shrink-0'
            />
            <span className='font-semibold text-neutral-900'>
              {t('pages.settings.planModal.termsCheckbox')}{' '}
              <a
                href='/terms-of-service#section-8'
                target='_blank'
                rel='noreferrer'
                className='underline text-sapphire'
              >
                Terms of Service
              </a>
              .
            </span>
          </label>
        </div>
      </Modal>

    </div>
  );
});
